import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { Button, Form, Input, Table, Switch } from 'antd';

import {
    createLocation,
    deleteLocation,
    getLocations,
    updateLocation,
} from 'apis/rest/agenda/locations';
import PageSpinner from 'components/app/common/pageSpinner';
import { openNotification } from 'Apps/VenueBuilder/helpers/openNotification';
import Empty from './common/Empty';

import styles from './index.module.scss';

import FormContainer from './common/FormContainer';
import SearchIcon from './common/SearchIcon';
import Prompt from '../../../../../../common/prompt';

const Locations = ({ search = '' }) => {
    const projectId = useSelector((state) => state?.header?.projectId);
    const [loading, setLoading] = useState(true);
    const [loadingTable, setLoadingTable] = useState(false);
    const [showDrawer, setShowDrawer] = useState(false);
    const [showEmpty, setShowEmpty] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');
    const [sortOrder, setSortOrder] = useState('ascend');
    const [pagination, setPagination] = useState({});
    const [locations, setLocations] = useState([]);

    const [form] = Form.useForm();
    const [formLoading, setFormLoading] = useState(false);
    const [formAction, setFormAction] = useState('create');
    const [formShouldContinue, setFormShouldContinue] = useState(false);
    const [formAPIFunction, setFormAPIFunction] = useState();

    async function openCreateLocationForm() {
        setFormAction('create');
        setFormAPIFunction(() => (name, showForSponsor) => {
            const inputs = {
                projectId,
                name,
                showForSponsor,
            };
            return createLocation(inputs);
        });
        form.setFieldsValue({ name: '', showForSponsor: true });
        setShowDrawer(true);
    }

    async function openUpdateLocationForm(location) {
        setFormAction('update');
        setFormAPIFunction(() => (name, showForSponsor) => {
            const inputs = {
                name,
                showForSponsor,
            };
            return updateLocation(projectId, location.id, inputs);
        });
        form.setFieldsValue({
            name: location?.name,
            showForSponsor: location?.showForSponsor,
        });

        setShowDrawer(true);
    }

    async function onFinish(values) {
        setFormLoading(true);
        try {
            const res = await formAPIFunction(
                values.name,
                values.showForSponsor
            );
            if (!res.status) {
                throw new Error(res.message);
            }

            openNotification('success', {
                message:
                    formAction === 'create'
                        ? 'You have successfully created the location'
                        : 'You have successfully updated the location',
            });

            if (!formShouldContinue) {
                setShowDrawer(false);
            }

            form.setFieldsValue({ name: '', showForSponsor: true });
        } catch (error) {
            console.error(error);
            openNotification('error', {
                message: error?.message || 'Something went wrong!',
            });
        } finally {
            setFormLoading(false);
            debouncedFetchData(pagination.current, searchFilter, sortOrder);
        }
    }

    const [deletingLocationId, setDeletingLocationId] = useState(null);
    const onDeleteLocationHandler = async () => {
        try {
            const res = await deleteLocation(projectId, deletingLocationId);
            if (!res.status) throw res;

            openNotification('success', {
                message: 'You have successfully deleted the location',
            });
        } catch (error) {
            console.error(error);
            openNotification('error', {
                message: error?.message || 'Something went wrong!',
            });
        } finally {
            debouncedFetchData(1, searchFilter, sortOrder);
            setDeletingLocationId(null);
        }
    };

    async function fetchData(currentPage, searchFilter, sortOrder) {
        try {
            const data = await getLocations(
                projectId,
                currentPage,
                searchFilter,
                sortOrder === 'ascend' ? 'asc' : 'desc'
            );
            setLocations(data.physicalVenues);
            setPagination({
                current: data.pagination.currentPage,
                total: data.pagination.total,
                pageSize: data.pagination.limit,
            });
            setShowEmpty(false);
        } catch (error) {
            console.error(error);
            openNotification('error', {
                message: error?.message || 'Something went wrong!',
            });
        } finally {
            setLoadingTable(false);
        }
    }

    const debouncedFetchData = useCallback(debounce(fetchData, 300), []);
    const handleSearch = (search) => {
        setSearchFilter(search);
        setPagination({ current: 1 });
        setLoadingTable(true);
        debouncedFetchData(pagination.current, search, sortOrder);
    };

    useEffect(() => {
        async function fetchInitialData() {
            const data = await getLocations(projectId, 1, '', 'asc');
            setLocations(data.physicalVenues);
            setPagination({
                current: data.pagination.currentPage,
                total: data.pagination.total,
                pageSize: data.pagination.limit,
            });

            if (!data.pagination.total) {
                setShowEmpty(true);
            }

            setLoading(false);
        }

        fetchInitialData();
    }, []);

    useEffect(() => {
        handleSearch(search || '');
    }, [search]);

    return loading ? (
        <PageSpinner type="Oval" color="#ACBDC9" height={45} width={45} />
    ) : (
        <div
            className={`${styles.meetingsLocationContainer} tw-p-4 tw-bg-white tw-mt-0 md:tw-pt-7 tw-h-[70vh] sm:tw-h-auto`}
        >
            {showEmpty ? (
                <Empty
                    heading="No Locations"
                    subHead1="For meetings with onsite physical locations, you can specify the name of the location where the meeting is supposed to happen."
                    contentMaxWidth="400px"
                    showButton={false}
                />
            ) : (
                <>
                    <div className={styles.meetingsLocationControls}>
                        <Input
                            allowClear
                            placeholder="Search"
                            prefix={<SearchIcon height={16} width={16} />}
                            onChange={(event) => {
                                handleSearch(event.target.value);
                            }}
                            onPressEnter={(event) => {
                                handleSearch(event.target.value);
                            }}
                        />
                    </div>
                    <div>
                        {pagination.total ? (
                            <Table
                                className="agenda-location-table"
                                columns={[
                                    {
                                        title: 'Location Name',
                                        dataIndex: 'name',
                                        defaultSortOrder: sortOrder,
                                        sorter: true,
                                        sortDirections: [
                                            'ascend',
                                            'descend',
                                            'ascend',
                                        ],
                                        render: (text) => {
                                            return (
                                                <div
                                                    className={
                                                        styles.meetingsLocationTableRow
                                                    }
                                                >
                                                    <p>{text}</p>
                                                </div>
                                            );
                                        },
                                    },
                                ]}
                                dataSource={locations}
                                pagination={{ ...pagination, align: 'center' }}
                                onChange={(pagination, filters, sorter) => {
                                    setSortOrder(sorter.order);
                                    debouncedFetchData(
                                        pagination.current,
                                        searchFilter,
                                        sorter.order
                                    );
                                }}
                            />
                        ) : loadingTable ? (
                            <PageSpinner
                                type="Oval"
                                color="#ACBDC9"
                                height={45}
                                width={45}
                            />
                        ) : (
                            <Empty
                                heading={
                                    searchFilter
                                        ? `No locations found for "${searchFilter}"`
                                        : `No locations found`
                                }
                                contentMaxWidth="400px"
                                subHead1="For meetings with onsite physical locations, you can specify the name of the location where the meeting is supposed to happen."
                                showButton={false}
                            />
                        )}
                    </div>
                </>
            )}
            <style>{`
                .ant-table thead tr th {
                    padding: 0px !important;

                    font-family: Open Sans;
                    font-style: normal;
                    font-weight: 600;
                    font-size: 14px;
                    line-height: 143.87%;
                    /* or 20px */

                    letter-spacing: -0.02em;
                }

                .ant-table tbody tr td {
                    padding: 16px !important;
                }

                input.ant-input {
                    margin-bottom: 0;
                }

                .agenda-location-table .ant-pagination{
                    margin-right: 30px!important;
                    justify-content: center !important;

                }
           
            `}</style>
        </div>
    );
};

export default Locations;
